import { sha256HexSync } from "./sha256Sync";
import {
  buildRecipeContentId,
  readStructuredRecipeIngredients,
} from "./structuredRecipe";

const DAYS = Object.freeze([
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
  "sunday",
]);
const DEFAULT_PREFERRED_DAYS = Object.freeze({
  1: ["sunday"],
  2: ["sunday", "wednesday"],
  3: ["sunday", "tuesday", "thursday"],
});
const SESSION_DAY_GROUPS = Object.freeze({
  1: [["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"]],
  2: [
    ["monday", "tuesday", "wednesday"],
    ["thursday", "friday", "saturday", "sunday"],
  ],
  3: [
    ["monday", "tuesday"],
    ["wednesday", "thursday"],
    ["friday", "saturday", "sunday"],
  ],
});

function cleanText(value, maxLength) {
  if (typeof value !== "string") return null;
  const text = value.replace(/\s+/g, " ").trim();
  return text && text.length <= maxLength ? text : null;
}

function cleanId(value) {
  const id = cleanText(value, 120);
  return id && /^[a-z0-9][a-z0-9:_-]*$/i.test(id) ? id : null;
}

function normalizedToken(value) {
  return String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLocaleLowerCase("fr")
    .replace(/\s+/g, " ")
    .trim();
}

const DAY_ALIASES = new Map([
  ["monday", "monday"], ["lundi", "monday"],
  ["tuesday", "tuesday"], ["mardi", "tuesday"],
  ["wednesday", "wednesday"], ["mercredi", "wednesday"],
  ["thursday", "thursday"], ["jeudi", "thursday"],
  ["friday", "friday"], ["vendredi", "friday"],
  ["saturday", "saturday"], ["samedi", "saturday"],
  ["sunday", "sunday"], ["dimanche", "sunday"],
]);

function normalizeDay(meal) {
  if (Number.isInteger(meal?.dayIndex) && meal.dayIndex >= 1 && meal.dayIndex <= 7) {
    return DAYS[meal.dayIndex - 1];
  }
  return DAY_ALIASES.get(normalizedToken(meal?.day || meal?.dayLabel)) || null;
}

function normalizedPreparation(value) {
  return normalizedToken(value);
}

function roundQuantity(value) {
  return value === null ? null : Math.round(value * 1_000_000) / 1_000_000;
}

function baseAmount(ingredient, multiplier = 1) {
  if (ingredient.quantity === null) return { quantity: null, unit: ingredient.unit };
  if (ingredient.unit === "kg") return { quantity: roundQuantity(ingredient.quantity * 1000 * multiplier), unit: "g" };
  if (ingredient.unit === "l") return { quantity: roundQuantity(ingredient.quantity * 1000 * multiplier), unit: "ml" };
  return { quantity: roundQuantity(ingredient.quantity * multiplier), unit: ingredient.unit };
}

function addQuantities(left, right) {
  if (left === null || right === null) return null;
  return roundQuantity(left + right);
}

function sortedUnique(values) {
  return Array.from(new Set(values)).sort((a, b) => a.localeCompare(b, "fr"));
}

function validPeople(value) {
  return Number.isInteger(value) && value >= 1 && value <= 20;
}

function referenceServings(recipe) {
  return typeof recipe?.servings === "number"
    && Number.isFinite(recipe.servings)
    && recipe.servings > 0
    && recipe.servings <= 100
    ? recipe.servings
    : 1;
}

function mergeCategory(current, next) {
  if (!current || current === "other") return next || "other";
  if (!next || next === "other") return current;
  return current === next ? current : "other";
}

function emptyFoundation(issue, people = 1) {
  return {
    version: 1,
    ready: false,
    people,
    components: [],
    usages: [],
    consolidatedIngredients: [],
    issues: [issue],
  };
}

/**
 * Fondation pure du Meal Prep modulaire. Elle ne choisit aucun aliment : elle
 * groupe uniquement les ingrédients des recettes déjà présentes dans `meals`.
 * Les quantités suivent toujours : quantité recette × portions demandées /
 * portions de référence de la recette.
 */
export function buildModularMealPrepFoundation(meals, { people = 1 } = {}) {
  if (!validPeople(people)) return emptyFoundation("Nombre de personnes invalide.", people);
  if (!Array.isArray(meals) || meals.length === 0 || meals.length > 84) {
    return emptyFoundation("Repas à préparer absents ou trop nombreux.", people);
  }

  const issues = [];
  const normalizedMeals = [];
  const seenMealIds = new Set();
  for (const meal of meals) {
    const mealId = cleanId(meal?.id);
    const title = cleanText(meal?.title, 180);
    const ingredients = readStructuredRecipeIngredients(meal?.recipe);
    const recipeId = buildRecipeContentId(meal?.recipe);
    if (!mealId || !title || seenMealIds.has(mealId)) {
      issues.push("Un repas possède un identifiant ou un titre invalide.");
      continue;
    }
    seenMealIds.add(mealId);
    if (!ingredients || !recipeId) {
      issues.push(`La recette « ${title} » n’a pas encore d’ingrédients structurés complets.`);
      continue;
    }
    const requestedServings = typeof meal?.requiredServings === "number"
      ? meal.requiredServings
      : people;
    const recipeServings = referenceServings(meal.recipe);
    if (!Number.isFinite(requestedServings) || requestedServings <= 0 || requestedServings > 100) {
      issues.push(`Le nombre de portions de « ${title} » est invalide.`);
      continue;
    }
    normalizedMeals.push({
      id: mealId,
      title,
      recipeId,
      day: normalizeDay(meal),
      dayLabel: cleanText(meal?.dayLabel, 60),
      requiredServings: requestedServings,
      recipeServings,
      multiplier: requestedServings / recipeServings,
      ingredients,
    });
  }

  if (issues.length > 0 || normalizedMeals.length !== meals.length) {
    return {
      ...emptyFoundation(sortedUnique(issues)[0] || "Recettes à structurer.", people),
      issues: sortedUnique(issues),
    };
  }

  const componentMap = new Map();
  const usageMap = new Map();
  const shoppingMap = new Map();

  for (const meal of normalizedMeals) {
    for (const ingredient of meal.ingredients) {
      const amount = baseAmount(ingredient, meal.multiplier);
      const preparationKey = normalizedPreparation(ingredient.preparation);
      const componentKey = `${ingredient.foodKey}|${amount.unit}|${preparationKey}`;
      const componentId = `component-${sha256HexSync(componentKey).slice(0, 20)}`;
      const currentComponent = componentMap.get(componentKey) || {
        id: componentId,
        foodKey: ingredient.foodKey,
        label: ingredient.label,
        category: ingredient.category || "other",
        quantity: amount.quantity,
        unit: amount.unit,
        preparation: ingredient.preparation,
        mealIds: [],
        recipeIds: [],
      };
      if (componentMap.has(componentKey)) {
        currentComponent.quantity = addQuantities(currentComponent.quantity, amount.quantity);
        currentComponent.category = mergeCategory(currentComponent.category, ingredient.category);
      }
      currentComponent.mealIds.push(meal.id);
      currentComponent.recipeIds.push(meal.recipeId);
      componentMap.set(componentKey, currentComponent);

      const usageKey = `${componentId}|${meal.id}`;
      const currentUsage = usageMap.get(usageKey) || {
        componentId,
        mealId: meal.id,
        recipeId: meal.recipeId,
        mealTitle: meal.title,
        day: meal.day,
        quantity: 0,
        unit: amount.unit,
        portions: meal.requiredServings,
        ...(meal.dayLabel ? { dayLabel: meal.dayLabel } : {}),
      };
      currentUsage.quantity = addQuantities(currentUsage.quantity, amount.quantity);
      usageMap.set(usageKey, currentUsage);

      const shoppingKey = `${ingredient.foodKey}|${amount.unit}`;
      const currentShopping = shoppingMap.get(shoppingKey) || {
        foodKey: ingredient.foodKey,
        label: ingredient.label,
        category: ingredient.category || "other",
        quantity: amount.quantity,
        unit: amount.unit,
        mealIds: [],
      };
      if (shoppingMap.has(shoppingKey)) {
        currentShopping.quantity = addQuantities(currentShopping.quantity, amount.quantity);
        currentShopping.category = mergeCategory(currentShopping.category, ingredient.category);
      }
      currentShopping.mealIds.push(meal.id);
      shoppingMap.set(shoppingKey, currentShopping);
    }
  }

  const allUsages = Array.from(usageMap.values());
  const components = Array.from(componentMap.values())
    .map((component) => {
      const mealIds = sortedUnique(component.mealIds);
      const portions = allUsages
        .filter((usage) => usage.componentId === component.id)
        .reduce((sum, usage) => sum + usage.portions, 0);
      return {
        ...component,
        portions,
        mealIds,
        recipeIds: sortedUnique(component.recipeIds),
        reusable: mealIds.length > 1,
      };
    })
    .sort((a, b) => a.id.localeCompare(b.id));
  const usages = allUsages
    .sort((a, b) => `${a.mealId}|${a.componentId}`.localeCompare(`${b.mealId}|${b.componentId}`));
  const consolidatedIngredients = Array.from(shoppingMap.values())
    .map((ingredient) => ({ ...ingredient, mealIds: sortedUnique(ingredient.mealIds) }))
    .sort((a, b) => `${a.foodKey}|${a.unit}`.localeCompare(`${b.foodKey}|${b.unit}`));

  return {
    version: 1,
    ready: true,
    people,
    components,
    usages,
    consolidatedIngredients,
    issues: [],
  };
}

function resolvePreferredDays(sessionCount, preferredDays) {
  const defaults = DEFAULT_PREFERRED_DAYS[sessionCount];
  const provided = Array.isArray(preferredDays)
    ? preferredDays.map((day) => DAY_ALIASES.get(normalizedToken(day))).filter(Boolean)
    : [];
  const unique = Array.from(new Set(provided));
  for (const fallback of defaults) {
    if (unique.length >= sessionCount) break;
    if (!unique.includes(fallback)) unique.push(fallback);
  }
  return unique.slice(0, sessionCount);
}

function buildSessionComponents(foundation, mealDays) {
  const componentsById = new Map(foundation.components.map((component) => [component.id, component]));
  const grouped = new Map();
  for (const usage of foundation.usages.filter((item) => mealDays.includes(item.day))) {
    const component = componentsById.get(usage.componentId);
    if (!component) continue;
    const current = grouped.get(usage.componentId) || {
      componentId: component.id,
      label: component.label,
      category: component.category,
      preparation: component.preparation,
      quantity: 0,
      unit: component.unit,
      portions: 0,
      usages: [],
    };
    current.quantity = addQuantities(current.quantity, usage.quantity);
    current.portions += usage.portions;
    current.usages.push({
      mealId: usage.mealId,
      mealTitle: usage.mealTitle,
      day: usage.day,
      quantity: usage.quantity,
      unit: usage.unit,
      portions: usage.portions,
    });
    grouped.set(usage.componentId, current);
  }
  return Array.from(grouped.values()).sort((a, b) => a.componentId.localeCompare(b.componentId));
}

/** Entrée unique du moteur Meal Prep v2. Aucune donnée n’est persistée ici. */
export function buildModularMealPrep({
  meals,
  recipes = {},
  people = 1,
  sessionCount = 2,
  preferredDays = [],
} = {}) {
  if (!Number.isInteger(sessionCount) || sessionCount < 1 || sessionCount > 3) {
    return {
      version: 2,
      ready: false,
      people,
      sessionCount,
      sessions: [],
      components: [],
      usages: [],
      shoppingList: [],
      issues: ["Le nombre de sessions doit être compris entre 1 et 3."],
    };
  }
  const sourceMeals = Array.isArray(meals) ? meals : [];
  const mealsWithRecipes = sourceMeals.map((meal) => {
    const recipeKey = meal?.recipeId || meal?.recipeKey || meal?.id;
    return {
      ...meal,
      recipe: meal?.recipe || recipes?.[recipeKey],
    };
  });
  const foundation = buildModularMealPrepFoundation(mealsWithRecipes, { people });
  const missingDay = foundation.ready && foundation.usages.some((usage) => !usage.day);
  if (!foundation.ready || missingDay) {
    return {
      version: 2,
      ready: false,
      people,
      sessionCount,
      sessions: [],
      components: [],
      usages: [],
      shoppingList: [],
      issues: missingDay
        ? ["Chaque repas doit être rattaché à un jour de la semaine."]
        : foundation.issues,
    };
  }

  const preferred = resolvePreferredDays(sessionCount, preferredDays);
  const sessions = SESSION_DAY_GROUPS[sessionCount].map((mealDays, index) => ({
    id: `session-${index + 1}`,
    label: `Session ${index + 1}`,
    preferredDay: preferred[index],
    mealDays,
    components: buildSessionComponents(foundation, mealDays),
  }));
  const shoppingList = foundation.consolidatedIngredients.map((ingredient) => ({
    category: ingredient.category || "other",
    foodKey: ingredient.foodKey,
    label: ingredient.label,
    quantity: ingredient.quantity,
    unit: ingredient.unit,
  }));

  return {
    version: 2,
    ready: true,
    people,
    sessionCount,
    preferredDays: preferred,
    sessions,
    components: foundation.components,
    usages: foundation.usages,
    shoppingList,
    issues: [],
  };
}
